import type { IntegerAmount } from '@actual-app/core/shared/util';

/**
 * Cálculos puros de las tarjetas «Cómo va el mes» y «Se pasan» (Informes).
 * Todo en céntimos enteros y en positivo (lo gastado es un gasto, no un
 * importe con signo).
 */

export type EstadoRitmo = 'bien' | 'justo' | 'pasa';

/** Margen antes de pasar de «Vas bien» a «Vas justo» y a «Te pasas». */
export const TOLERANCIA_BIEN = 1.03;
export const TOLERANCIA_JUSTO = 1.12;

/**
 * Lo normal llevado a día `dia` de un mes de `diasMes` días: los fijos (que
 * se pagan de golpe) desde el día 1 y el resto repartido por igual.
 */
export function normalHastaDia(
  referencia: IntegerAmount,
  fijos: IntegerAmount,
  dia: number,
  diasMes: number,
): IntegerAmount {
  const fijo = Math.min(Math.max(fijos, 0), referencia);
  const fraccion = Math.min(Math.max(dia / diasMes, 0), 1);
  return Math.round(fijo + (referencia - fijo) * fraccion);
}

/**
 * Cómo va el mes frente a su referencia (el plan, o lo habitual si no hay
 * nada asignado). Pasarse de la referencia es siempre «Te pasas».
 */
export function estadoRitmo({
  gastado,
  referencia,
  fijos,
  dia,
  diasMes,
}: {
  gastado: IntegerAmount;
  referencia: IntegerAmount;
  fijos: IntegerAmount;
  dia: number;
  diasMes: number;
}): { estado: EstadoRitmo; queda: IntegerAmount; normalHoy: IntegerAmount } {
  const queda = referencia - gastado;
  const normalHoy = normalHastaDia(referencia, fijos, dia, diasMes);
  if (referencia <= 0) {
    return { estado: gastado > 0 ? 'pasa' : 'bien', queda, normalHoy };
  }
  if (gastado > referencia) {
    return { estado: 'pasa', queda, normalHoy };
  }
  const razon = normalHoy > 0 ? gastado / normalHoy : 0;
  const estado: EstadoRitmo =
    razon <= TOLERANCIA_BIEN
      ? 'bien'
      : razon <= TOLERANCIA_JUSTO
        ? 'justo'
        : 'pasa';
  return { estado, queda, normalHoy };
}

/**
 * Gasto acumulado día a día (índice 0 = día 1) hasta `dia`, a partir del
 * gasto de cada día (`'yyyy-MM-dd'` → céntimos en positivo).
 */
export function acumuladoPorDia(
  gastoDia: ReadonlyMap<string, IntegerAmount>,
  mes: string,
  dia: number,
): IntegerAmount[] {
  const serie: IntegerAmount[] = [];
  let suma = 0;
  for (let d = 1; d <= dia; d++) {
    suma += gastoDia.get(`${mes}-${String(d).padStart(2, '0')}`) ?? 0;
    serie.push(suma);
  }
  return serie;
}

/** Saldo de una categoría en el Plan del mes. */
export type SaldoCategoria = {
  id: string;
  /** Lo que tenía para gastar este mes (asignado + lo que traía). */
  disponible: IntegerAmount;
  /** Lo gastado este mes, en positivo. */
  gastado: IntegerAmount;
  /** Lo que le queda: negativo si está en rojo. */
  saldo: IntegerAmount;
  esFijo: boolean;
};

export type CategoriaPasada = {
  id: string;
  tipo: 'pasada' | 'justa';
  gastado: IntegerAmount;
  disponible: IntegerAmount;
  /** Pasada: lo que se ha pasado. Justa: lo que le queda. */
  importe: IntegerAmount;
};

/**
 * Categorías que se han pasado (saldo en rojo) y las que, al ritmo de este
 * mes, se pasarán antes de que acabe («justas»). Los fijos no se proyectan:
 * se pagan de una vez. Primero las pasadas (más rojo primero) y luego las
 * justas (menos margen primero).
 */
export function categoriasQueSePasan(
  saldos: readonly SaldoCategoria[],
  dia: number,
  diasMes: number,
): { pasadas: CategoriaPasada[]; justas: CategoriaPasada[]; bien: number } {
  const pasadas: CategoriaPasada[] = [];
  const justas: CategoriaPasada[] = [];
  let bien = 0;
  const fraccion = Math.min(Math.max(dia / diasMes, 0.01), 1);
  for (const s of saldos) {
    if (s.disponible <= 0 && s.gastado <= 0) {
      continue;
    }
    if (s.saldo < 0) {
      pasadas.push({
        id: s.id,
        tipo: 'pasada',
        gastado: s.gastado,
        disponible: s.disponible,
        importe: -s.saldo,
      });
      continue;
    }
    const proyeccion = s.gastado / fraccion;
    if (
      !s.esFijo &&
      dia < diasMes &&
      s.gastado > 0 &&
      proyeccion > s.disponible
    ) {
      justas.push({
        id: s.id,
        tipo: 'justa',
        gastado: s.gastado,
        disponible: s.disponible,
        importe: s.saldo,
      });
      continue;
    }
    bien++;
  }
  pasadas.sort((a, b) => b.importe - a.importe);
  justas.sort((a, b) => a.importe - b.importe);
  return { pasadas, justas, bien };
}
